import { Button, Grid, Chip, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import { GuidelinesIndexEntry } from '../model/GuidelinesIndex';
import { guidelinePathToUrlParam } from '../services/GuidelinePathToUrlParam';
import {
  doesTitleIncludeFph,
  doesTitleIncludeHw,
  doesTitleIncludeWph,
  removeSiteSpecifierFromName,
} from './ThemeAndTitleProvider';
import { useGuidelinesLoaderProviderContext } from '../services/GuidelinesLoaderProviderContext';
import { categorizeGuidelines } from '../services/GuidelinesIndexUtils';

interface SearchGuidelinesSearchAndResultsProps {
  searchText: string;
  handleClose: () => void;
}

export function SearchGuidelinesSearchAndResults({
  searchText,
  handleClose,
}: SearchGuidelinesSearchAndResultsProps): JSX.Element {
  const [error, setError] = useState('');

  const { loadGuidelinesIndex } = useGuidelinesLoaderProviderContext();

  const [
    guidelineEntriesByCategoryAndFolder,
    setGuidelineEntriesByCategoryAndFolder,
  ] = useState(new Map<string, GuidelinesIndexEntry[]>());

  const getGuidelinesIndex = useCallback(async (): Promise<void> => {
    try {
      if (!loadGuidelinesIndex) throw new Error('No guidelines provider');
      const guidelinesIndex = await loadGuidelinesIndex();
      const tempGuidelineEntriesByCategoryAndFolder = categorizeGuidelines(
        guidelinesIndex.indexEntries,
        false,
        'CategoryAndSubfolder'
      );
      setGuidelineEntriesByCategoryAndFolder(
        tempGuidelineEntriesByCategoryAndFolder
      );
    } catch (innerError) {
      const typedError = innerError as { message: string };
      setError(typedError.message);
    }
  }, [loadGuidelinesIndex]);

  useEffect(() => {
    getGuidelinesIndex();
  }, [getGuidelinesIndex]);

  const [searchResultsElement, setSearchResultsElement] = useState(<div />);

  const performSearch = useCallback(async (): Promise<void> => {
    // Use setTimeout to allow the display to render before doing the search
    setTimeout(() => {
      const tempSearchResultsElement = buildSearchResults(
        guidelineEntriesByCategoryAndFolder,
        searchText,
        handleClose
      );
      setSearchResultsElement(tempSearchResultsElement);
    }, 0);
  }, [guidelineEntriesByCategoryAndFolder, handleClose, searchText]);

  useEffect(() => {
    performSearch();
  }, [guidelineEntriesByCategoryAndFolder, performSearch, searchText]);

  if (error !== '') {
    return <li>{error}</li>;
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{searchResultsElement}</>;
}

function buildSearchResults(
  guidelineEntriesByCategoryAndFolder: Map<string, GuidelinesIndexEntry[]>,
  searchText: string,
  handleClose: () => void
) {
  const containsSearchResults = filterGuidelinesIndex(
    guidelineEntriesByCategoryAndFolder,
    searchText.trim(),
    'contains'
  );

  // Doing a word match over the entire index is expensive, so only do it when
  // there are more than 2 search characters, and then only filter the
  // contains results
  let matchesWordSearchResultElements: JSX.Element[] = [];
  let matchesWordSearchResults: Map<string, GuidelinesIndexEntry[]> = new Map();
  const haveSearchText = searchText.trim().length > 1;
  if (haveSearchText) {
    matchesWordSearchResults = filterGuidelinesIndex(
      containsSearchResults,
      searchText.trim(),
      'exact'
    );
    matchesWordSearchResultElements = buildSearchResultElements(
      matchesWordSearchResults,
      searchText.trim(),
      handleClose
    );
  }

  // Filter the duplicates out of the contains results
  const containsSearchResultsWithoutWordMatches = filterOutIndexDuplicates(
    containsSearchResults,
    matchesWordSearchResults
  );

  const containsSearchResultElements = buildSearchResultElements(
    containsSearchResultsWithoutWordMatches,
    searchText.trim(),
    handleClose
  );

  const hasResults =
    containsSearchResultElements.length > 0 ||
    matchesWordSearchResultElements.length > 0;

  return hasResults ? (
    <>
      {matchesWordSearchResultElements}
      {containsSearchResultElements}
    </>
  ) : (
    <Typography>{`No matches for '${searchText}'`}</Typography>
  );
}

function guidelineNameContainsSearchText(
  indexEntry: GuidelinesIndexEntry,
  searchText: string
) {
  return indexEntry.guidelineName
    .toLocaleLowerCase()
    .includes(searchText.toLocaleLowerCase());
}

function escapeRegExp(text: string) {
  return text.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
}

function guidelineNameContainsExactWord(
  text: string,
  searchText: string
): boolean {
  const regex = new RegExp(`\\b${escapeRegExp(searchText)}\\b`, 'i');

  return regex.test(text);
}

function filterGuidelinesIndex(
  guidelineEntriesByCategory: Map<string, GuidelinesIndexEntry[]>,
  searchText: string,
  searchType: 'exact' | 'contains'
) {
  const filteredEntries = new Map<string, GuidelinesIndexEntry[]>();

  guidelineEntriesByCategory.forEach(
    (entriesInCategoryAndSubfolder, categoryAndSubfolderName) => {
      const indexEntriesContainingSearchText =
        entriesInCategoryAndSubfolder.filter((indexEntry) =>
          searchType === 'contains'
            ? guidelineNameContainsSearchText(indexEntry, searchText)
            : guidelineNameContainsExactWord(
                indexEntry.guidelineName,
                searchText
              )
        );

      const hasResultsInThisFolder =
        indexEntriesContainingSearchText.length > 0;

      if (hasResultsInThisFolder) {
        filteredEntries.set(
          categoryAndSubfolderName,
          indexEntriesContainingSearchText
        );
      }
    }
  );

  return filteredEntries;
}

function filterOutIndexDuplicates(
  toFilter: Map<string, GuidelinesIndexEntry[]>,
  toCompare: Map<string, GuidelinesIndexEntry[]>
) {
  const filteredEntries = new Map<string, GuidelinesIndexEntry[]>();

  toFilter.forEach(
    (entriesInCategoryAndSubfolder, categoryAndSubfolderName) => {
      let filteredIndex = entriesInCategoryAndSubfolder;
      if (toCompare.has(categoryAndSubfolderName)) {
        const possibleDuplicates = toCompare.get(categoryAndSubfolderName)!;
        filteredIndex = entriesInCategoryAndSubfolder.filter(
          (indexEntry) => !possibleDuplicates.includes(indexEntry)
        );
      }
      if (filteredIndex.length > 0) {
        filteredEntries.set(categoryAndSubfolderName, filteredIndex);
      }
    }
  );

  return filteredEntries;
}

function buildSearchResultElements(
  guidelineEntriesByCategory: Map<string, GuidelinesIndexEntry[]>,
  searchText: string,
  handleClose: () => void
) {
  const categoryElements: JSX.Element[] = [];

  guidelineEntriesByCategory.forEach(
    (indexEntriesContainingSearchText, categoryAndSubfolderName) => {
      const folderElements = buildFolderResultElements(
        indexEntriesContainingSearchText,
        searchText,
        categoryAndSubfolderName,
        handleClose
      );

      categoryElements.push(...folderElements);
    }
  );

  return categoryElements;
}

// Would be good to read this rather than hard code it
const dialogContentPadding = '24px';

function buildFolderResultElements(
  entriesInCategoryAndSubfolder: GuidelinesIndexEntry[],
  searchText: string,
  categoryAndSubfolderName: string,
  handleClose: () => void
) {
  const key = `${categoryAndSubfolderName}-${searchText}`;

  const headerElement = (
    <Grid
      item
      xs={12}
      key={key}
      bgcolor="lightgrey"
      style={{
        // Go full width by setting our margin to the opposite
        // of the parent's padding
        marginLeft: `-${dialogContentPadding}`,
        marginRight: `-${dialogContentPadding}`,
        paddingLeft: '1rem',
      }}
    >
      <Typography variant="body2">{categoryAndSubfolderName}</Typography>
    </Grid>
  );

  const buttonElements = entriesInCategoryAndSubfolder.map((guidelineEntry) =>
    buildGuidelineButton(guidelineEntry, searchText, handleClose)
  );

  const folderElements = [headerElement, ...buttonElements];
  return folderElements;
}

function buildGuidelineButton(
  guidelineEntry: GuidelinesIndexEntry,
  searchText: string,
  handleClose: () => void
) {
  const safeUrl = guidelinePathToUrlParam(guidelineEntry.path);

  // TODO: Abstract this out of the core code
  const [nameToDisplay, badgeElement] = getSiteSpecificNameAndBadge(
    guidelineEntry.guidelineName
  );

  const nameToDisplayWithEmphasis = boldMatchedText(nameToDisplay, searchText);

  return (
    <Grid item xs={12} sm={6} key={guidelineEntry.key}>
      <Button
        component={RouterLink}
        to={safeUrl}
        style={{ textTransform: 'none', justifyContent: 'flex-start' }}
        onClick={handleClose}
      >
        {badgeElement}
        {nameToDisplayWithEmphasis}
      </Button>
    </Grid>
  );
}

function boldMatchedText(text: string, searchText: string) {
  const parts = text.split(new RegExp(`(${searchText})`, 'gi'));

  return (
    <span>
      {parts.map((part) =>
        part.toLowerCase() === searchText.toLowerCase() ? (
          <span className="text-slate-500 font-bold">{part}</span>
        ) : (
          part
        )
      )}
    </span>
  );
}

function getSiteSpecificNameAndBadge(
  guidelineName: string
): [string, JSX.Element] {
  const titleIncludesFph = doesTitleIncludeFph(guidelineName);
  const titleIncludesWph = doesTitleIncludeWph(guidelineName);
  const titleIncludesHw = doesTitleIncludeHw(guidelineName);

  const badgeElements: JSX.Element[] = [];

  if (titleIncludesFph) {
    badgeElements.push(
      <Chip
        size="small"
        color="frimley"
        label="FP"
        key="FP"
        sx={{ marginRight: '0.5rem' }}
      />
    );
  }

  if (titleIncludesWph) {
    badgeElements.push(
      <Chip
        size="small"
        color="wexham"
        label="WP"
        key="WP"
        sx={{ marginRight: '0.5rem' }}
      />
    );
  }

  if (titleIncludesHw) {
    badgeElements.push(
      <Chip
        size="small"
        color="heatherwood"
        label="HW"
        key="HW"
        sx={{ marginRight: '0.5rem' }}
      />
    );
  }

  const nameToDisplay = removeSiteSpecifierFromName(guidelineName).replace(
    '!',
    ''
  );

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return [nameToDisplay, <>{badgeElements}</>];
}
